import React from 'react';

// Palette / fonts cohérents avec BoutiqueInfluenceurs
const BG = '#070707';
const SURFACE = '#0e0e0e';
const BORDER = 'rgba(212, 175, 55, 0.18)';
const BORDER_STRONG = 'rgba(212, 175, 55, 0.45)';
const TEXT = '#ece6d3';
const TEXT_MUTED = '#9a9485';
const GOLD_LIGHT = '#e8b842';

const FONTS_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,opsz,wght@0,6..96,400;0,6..96,500;0,6..96,600;0,6..96,700;0,6..96,800&family=Inter:wght@300;400;500;600;700;800&display=swap');`;

const SECTIONS = [
  {
    title: 'Données collectées',
    paragraphs: [
      "Nous collectons uniquement les informations strictement nécessaires à la fourniture de nos services : nom, prénom, adresse email, numéro de téléphone, raison sociale, brief de campagne, et historique de commandes.",
      "Ces données sont fournies volontairement par vous lors de la création d'un compte, d'une commande, ou d'un échange avec notre équipe."
    ]
  },
  {
    title: 'Utilisation des données',
    paragraphs: [
      "Vos données nous servent à : traiter vos commandes, vous tenir informé de l'avancement de vos campagnes, vous proposer des recommandations d'égéries pertinentes, et faciliter le service client.",
      "Aucune donnée n'est utilisée pour du profilage publicitaire externe."
    ]
  },
  {
    title: 'Partage des données',
    paragraphs: [
      "Nous ne vendons ni ne louons vos données à des tiers. Vos informations peuvent être partagées uniquement avec les égéries et partenaires nécessaires à l'exécution d'une campagne (sur la base d'un contrat strict de confidentialité), ainsi qu'avec nos prestataires techniques (hébergement, paiement, emailing) tenus aux mêmes obligations."
    ]
  },
  {
    title: 'Cookies et technologies similaires',
    paragraphs: [
      "Nous utilisons des cookies essentiels au bon fonctionnement du site, ainsi que des cookies analytiques pour mesurer l'audience de manière anonyme.",
      "Vous pouvez à tout moment ajuster vos préférences via la page Consent Preferences."
    ]
  },
  {
    title: 'Vos droits (RGPD)',
    paragraphs: [
      "Conformément au Règlement Général sur la Protection des Données, vous disposez des droits suivants : accès, rectification, suppression, opposition, limitation et portabilité de vos données personnelles.",
      "Pour exercer ces droits, écrivez-nous à contact@e-application.fr. Nous répondons sous 30 jours maximum."
    ]
  },
  {
    title: 'Sécurité',
    paragraphs: [
      "Vos données sont stockées sur des serveurs sécurisés en Europe, chiffrement TLS pour les transmissions, et accès strictement limité aux personnes habilitées."
    ]
  },
  {
    title: 'Conservation',
    paragraphs: [
      "Vos données sont conservées pendant la durée de la relation contractuelle, puis archivées pendant la durée légale applicable (3 ans pour les données prospects, 10 ans pour les pièces comptables)."
    ]
  },
  {
    title: 'Contact',
    paragraphs: [
      "Pour toute question concernant cette politique de confidentialité ou vos données, contactez-nous à contact@e-application.fr."
    ]
  }
];

function PrivacyPolicy() {
  return (
    <div style={{
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      minHeight: '100vh',
      color: TEXT,
      background: `radial-gradient(ellipse at 20% 10%, rgba(212,175,55,0.08) 0%, transparent 45%), ${BG}`,
      overflowX: 'hidden'
    }}>
      <style>{FONTS_IMPORT}</style>

      {/* Header */}
      <header style={{
        padding: 'clamp(50px, 7vw, 90px) 20px clamp(28px, 4vw, 48px)',
        textAlign: 'center',
        borderBottom: `1px solid ${BORDER}`
      }}>
        <a href="/boutique-influenceurs" style={{
          display: 'inline-block',
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 3,
          textTransform: 'uppercase',
          color: GOLD_LIGHT,
          textDecoration: 'none',
          marginBottom: 24,
          opacity: 0.85
        }}>← Retour</a>
        <h1 style={{
          fontFamily: "'Bodoni Moda', 'Didot', serif",
          fontSize: 'clamp(32px, 5vw, 56px)',
          fontWeight: 700,
          letterSpacing: '-0.5px',
          textTransform: 'uppercase',
          color: '#ffffff',
          margin: '0 0 16px',
          lineHeight: 1.1
        }}>
          Privacy Policy
        </h1>
        <div style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 3,
          textTransform: 'uppercase',
          color: TEXT_MUTED
        }}>
          Politique de confidentialité — Dernière mise à jour {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
        </div>
        <div style={{
          width: 80,
          height: 1,
          background: `linear-gradient(90deg, transparent 0%, ${BORDER_STRONG} 50%, transparent 100%)`,
          margin: '24px auto 0'
        }}/>
      </header>

      {/* Body */}
      <main style={{
        maxWidth: 820,
        margin: '0 auto',
        padding: 'clamp(40px, 6vw, 72px) 20px'
      }}>
        {SECTIONS.map((sec, i) => (
          <section key={sec.title} style={{ marginBottom: 44 }}>
            <h2 style={{
              fontFamily: "'Bodoni Moda', 'Didot', serif",
              fontSize: 'clamp(20px, 2.4vw, 28px)',
              fontWeight: 700,
              letterSpacing: '-0.2px',
              textTransform: 'uppercase',
              color: '#ffffff',
              margin: '0 0 6px',
              display: 'flex',
              alignItems: 'baseline',
              gap: 14
            }}>
              <span style={{ color: GOLD_LIGHT, fontSize: '0.6em', fontWeight: 600, letterSpacing: 2 }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span>{sec.title}</span>
            </h2>
            <div style={{
              width: 40,
              height: 1,
              background: BORDER_STRONG,
              margin: '4px 0 18px 36px'
            }}/>
            {sec.paragraphs.map((p, j) => (
              <p key={j} style={{
                fontSize: 'clamp(13px, 1.4vw, 15px)',
                fontWeight: 400,
                lineHeight: 1.85,
                color: '#d8d2c0',
                margin: '0 0 14px',
                paddingLeft: 36
              }}>
                {p}
              </p>
            ))}
          </section>
        ))}
      </main>

      {/* Footer mini */}
      <footer style={{
        padding: '36px 20px',
        textAlign: 'center',
        borderTop: `1px solid ${BORDER}`,
        fontSize: 11,
        fontWeight: 400,
        letterSpacing: 2,
        textTransform: 'uppercase',
        color: TEXT_MUTED
      }}>
        <a href="https://www.e-shop-pro.fr/" target="_blank" rel="noopener noreferrer" style={{
          fontFamily: "'Bodoni Moda', serif",
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 2,
          color: '#ffffff',
          textDecoration: 'none',
          marginBottom: 8,
          display: 'inline-block'
        }}>E SHOPS PRO</a>
        <div style={{ marginTop: 10, opacity: 0.85 }}>
          All rights reserved — Copyright © {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
